import {
  GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery,
  InvestorDetailsGetInvestorLegalEntitiesQuery
} from '@/__generated__/graphql';
import client from '@/apollo-client';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Accordion } from '@/components/basicComponents/accordion';
import { Banner } from '@/components/basicComponents/banner';
import { ISelectValue } from '@/components/basicComponents/select';
import { ITab } from '@/components/basicComponents/tabs';
import { SectionZeroState } from '@/components/basicComponents';
import { EditInvestorEntityDetailsTaxStatusOptionEnum } from '@/components/investors-page/editInvestorPage/types';
import { Loader } from '@/components/loaders/loader';
import { GET_BANK_ACCOUNTS_AND_CONTACTS_QUERY, RENDERING_TEMPLATES_QUERY } from '@/components/transactions-page/generateSignaturePacket-page/queries';
import { IInvestorDetailsStepForm } from '@/components/transactions-page/generateSignaturePacket-page/steps/investorDetails';
import {
  IDocumentationAttachment,
  IRenderingTemplate,
  IRenderTemplate,
  IRenderTemplatePage
} from '@/components/transactions-page/generateSignaturePacket-page/templateComponents/types';
import { useAuth } from '@/hooks/use-auth';
import { useMounted } from '@/hooks/use-mounted';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { TenantType } from '@trace-8/trace8-schema';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { InvestorDetailsLayout } from '../../investorDetailsLayout';
import { InvestorDetailsEntityTypeEnum } from '../../types';
import { DocumentationDetails } from './components/documentationDetails';
import { DocumentationZeroState } from './components/documentationZeroState';
import { PageRowsPreview } from './components/pageRowsPreview';
import { EditInvestorDetailsModalPage } from './modalPages/editInvestorDetailsModalPage';
import { EditSectionFieldsModalPage } from './modalPages/editSectionFieldsModalPage';
import { DOCUMENTATION_TAB_DATA_QUERY, GET_LEGAL_ENTITY_DOCUMENTATION_QUERY, UPDATE_LEGAL_ENTITY_DOCUMENTATION_MUTATION } from './queries';
import { RenderingTemplatesVariablesType } from './types';
import { getTemplateWithFormData } from './utils';

interface DocumentationTabProps {
  entityType: InvestorDetailsEntityTypeEnum;
  investorData: {
    investorName: string;
    advisorName: string;
    legalEntities: InvestorDetailsGetInvestorLegalEntitiesQuery['getInvestor']['legalEntities'];
    legalEntitiesTotalCount: number;
  };
  selectedGroupName: string;
  entitiesSelectOptions: ISelectValue[];
  tabs: ITab[];
  resetEntityType: () => void;
}

export const DocumentationTab = ({
  entityType,
  investorData,
  selectedGroupName,
  entitiesSelectOptions,
  tabs,
  resetEntityType
}: DocumentationTabProps) => {
  const params = useParams();
  const isMounted = useMounted();
  const { isMobile } = useResponsive();
  const { searchParams, makeScroll, saveScrollPostion } = useSearchParamsManager();

  const { user } = useAuth();
  const isPlatformManagerUser = user && user.tenant?.type === TenantType.PLATFORM_MANAGER;

  const entityId = params?.entityId || '';
  const position = searchParams.get('position') || 0;

  const [tenantId, setTenantId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [template, setTemplate] = useState<IRenderTemplate | null>(null);
  const [bankAccounts, setBankAccounts] = useState<GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['bankAccounts']>(
    []
  );
  const [contacts, setContacts] = useState<GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['contacts']>([]);
  const [showZeroState, setShowZeroState] = useState(true);
  const [documentationDetailsData, setDocumentationDetailsData] = useState<(IInvestorDetailsStepForm & { id: string }) | null>(null);
  const [renderingTemplatesVariables, setRenderingTemplatesVariables] = useState<RenderingTemplatesVariablesType | null>(null);
  const [legalEntityDocumentationData, setLegalEntityDocumentationData] = useState<any>(null);
  const [uploadedFiles, setUploadedFiles] = useState<IDocumentationAttachment[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentEditPage, setCurrentEditPage] = useState<IRenderTemplatePage | null>(null);
  const [modalPageType, setModalPageType] = useState('');

  const [updateLegalEntityDocumentation] = useMutation(UPDATE_LEGAL_ENTITY_DOCUMENTATION_MUTATION);

  const { networkStatus: documentationTabDataNetworkStatus, refetch: documentationTabDataRefetch } = useQuery(DOCUMENTATION_TAB_DATA_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      id: entityId
    },
    onCompleted: (data) => {
      const { id, category, entityType, eligibility, otherType, taxStatus, taxExempt, tenant } = data.legalEntity;
      const taxStatusValue = taxStatus
        ? EditInvestorEntityDetailsTaxStatusOptionEnum[taxStatus]
        : taxExempt
          ? EditInvestorEntityDetailsTaxStatusOptionEnum.US_TAX_EXEMPT
          : EditInvestorEntityDetailsTaxStatusOptionEnum.US_TAXABLE;

      setTenantId(tenant?.id);
      setShowZeroState(!entityType || !category);
      setDocumentationDetailsData({ id, category, entityType, eligibility, otherType, taxStatus: taxStatusValue });
      setRenderingTemplatesVariables(
        entityType && category
          ? {
              entityType,
              category,
              taxExempt: taxStatusValue === EditInvestorEntityDetailsTaxStatusOptionEnum.US_TAX_EXEMPT,
              ...(isPlatformManagerUser && { tenantId: tenant?.id })
            }
          : null
      );
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { networkStatus: renderingTemplatesNetworkStatus, refetch: renderingTemplatesRefetch } = useQuery<{
    renderingTemplates: IRenderingTemplate[];
  }>(RENDERING_TEMPLATES_QUERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !renderingTemplatesVariables,
    variables: {
      input: {
        defaults: true,
        ...renderingTemplatesVariables
      }
    },
    onCompleted: async (data) => {
      try {
        if (!data.renderingTemplates.length) {
          setErrorMessage('There are templates missing for this entity, please contact your administrator.');
          return;
        }

        const { data: legalEntityDocumentationData } = await client.query({
          query: GET_LEGAL_ENTITY_DOCUMENTATION_QUERY,
          variables: {
            id: entityId
          }
        });

        const templateId = data?.renderingTemplates?.[0]?.id ?? '';

        if (legalEntityDocumentationData?.getLegalEntityDocumentation) {
          const { templateId: existingTemplateId, ...rest } = legalEntityDocumentationData.getLegalEntityDocumentation;
          await updateLegalEntityDocumentation({
            variables: {
              input: {
                id: entityId,
                templateId,
                kyc: rest || {}
              }
            }
          });

          const template = data.renderingTemplates?.[0].template;
          const updatedLegalEntityDocumentationData = template?.pages?.reduce(
            (acc, page) => {
              if ('expectedData' in page) {
                const pageData = page.expectedData?.reduce(
                  (pageAcc, expectedDataItem: any) => {
                    return {
                      ...pageAcc,
                      [expectedDataItem.name]: documentationDetailsData.eligibility
                    };
                  },
                  acc?.[page.name] || {}
                );

                return {
                  ...acc,
                  [page.name]: pageData
                };
              }
              return acc;
            },
            { ...(legalEntityDocumentationData?.getLegalEntityDocumentation || {}) }
          );

          const updatedTemplatePages = getTemplateWithFormData(template?.pages ?? [], updatedLegalEntityDocumentationData);
          setTemplate({ ...template, pages: updatedTemplatePages });
          setTemplateId(templateId);
          setLegalEntityDocumentationData(legalEntityDocumentationData.getLegalEntityDocumentation);
          if (isMounted()) {
            makeScroll(position);
          }
          return;
        }

        await updateLegalEntityDocumentation({
          variables: {
            input: {
              id: entityId,
              templateId,
              kyc: {}
            }
          }
        });
        setTemplateWithoutLegalEntityDocumentation(data?.renderingTemplates?.[0]?.template, data?.renderingTemplates?.[0]?.id ?? '');
        if (isMounted()) {
          makeScroll(position);
        }
      } catch (error) {
        console.error(error);
        setTemplateWithoutLegalEntityDocumentation(data?.renderingTemplates?.[0]?.template, data?.renderingTemplates?.[0]?.id ?? '');
      }
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const { refetch: bankAccountsAndContactsRefetch } = useQuery(GET_BANK_ACCOUNTS_AND_CONTACTS_QUERY, {
    notifyOnNetworkStatusChange: true,
    variables: {
      id: entityId
    },
    onCompleted: (data) => {
      const { bankAccounts, contacts } = data.legalEntity;
      setBankAccounts(bankAccounts);
      setContacts(contacts);
    }
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/legal-entity-attachment/legal-entity/${entityId}/attachments`, {
          headers: { authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        setUploadedFiles(data as IDocumentationAttachment[]);
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);

  const setTemplateWithoutLegalEntityDocumentation = (template: IRenderTemplate, templateId: string) => {
    const updatedTemplatePages = template?.pages?.map((page: IRenderTemplatePage) => {
      if ('expectedData' in page) {
        page.expectedData?.forEach((expectedDataItem: any) => {
          page.formData = {
            ...(page?.formData ?? {}),
            [expectedDataItem.name]: documentationDetailsData.eligibility
          };
        });
      }
      return page;
    });
    setTemplate({ ...template, pages: updatedTemplatePages });
    setTemplateId(templateId);
  };

  const handleModalPage = ({ type, page }: { type: string; page: IRenderTemplatePage | null }) => {
    if (!!type) {
      saveScrollPostion();
    }
    setModalPageType(type);
    setCurrentEditPage(page ?? null);
  };

  const isLoading =
    renderingTemplatesNetworkStatus === NetworkStatus.loading ||
    renderingTemplatesNetworkStatus === NetworkStatus.setVariables ||
    documentationTabDataNetworkStatus === NetworkStatus.loading ||
    documentationTabDataNetworkStatus === NetworkStatus.setVariables;

  if (!!modalPageType) {
    return (
      <>
        {modalPageType === 'edit-section-fields' && (
          <EditSectionFieldsModalPage
            currentEditPage={currentEditPage}
            templateId={templateId}
            tenantId={tenantId}
            bankAccounts={bankAccounts}
            contacts={contacts}
            bankAccountsAndContactsRefetch={bankAccountsAndContactsRefetch}
            renderingTemplatesRefetch={renderingTemplatesRefetch}
            handleCloseEditPage={() => handleModalPage({ type: '', page: null })}
          />
        )}
        {modalPageType === 'edit-investor-details' && (
          <EditInvestorDetailsModalPage
            investorDetailsData={documentationDetailsData}
            documentationTabDataRefetch={documentationTabDataRefetch}
            handleCloseEditPage={() => handleModalPage({ type: '', page: null })}
          />
        )}
      </>
    );
  }

  if (showZeroState) {
    return (
      <InvestorDetailsLayout
        entityType={entityType}
        investorData={investorData}
        selectedGroupName={selectedGroupName}
        entitiesSelectOptions={entitiesSelectOptions}
        tabs={tabs}
        resetEntityType={resetEntityType}
      >
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <DocumentationDetails
              documentationDetailsData={documentationDetailsData}
              openInvestorDetailsPage={() => handleModalPage({ type: 'edit-investor-details', page: null })}
            />
            <DocumentationZeroState openInvestorDetailsPage={() => handleModalPage({ type: 'edit-investor-details', page: null })} />
          </>
        )}
      </InvestorDetailsLayout>
    );
  }

  return (
    <InvestorDetailsLayout
      entityType={entityType}
      investorData={investorData}
      selectedGroupName={selectedGroupName}
      entitiesSelectOptions={entitiesSelectOptions}
      tabs={tabs}
      resetEntityType={resetEntityType}
    >
      {isLoading ? (
        <Loader />
      ) : (
        <>
          {errorMessage && (
            <Banner
              icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
              title="Error"
              description={errorMessage}
              bgColor={themeVar('context.error')}
            />
          )}
          <DocumentationDetails
            documentationDetailsData={documentationDetailsData}
            openInvestorDetailsPage={() => handleModalPage({ type: 'edit-investor-details', page: null })}
          />
          <PaddingWrap>
            <SectionsWrapper>
              {template?.pages?.map((page: IRenderTemplatePage) => (
                <Accordion key={page.title} title={page.title} onEdit={() => handleModalPage({ type: 'edit-section-fields', page })} collapsable>
                  {page?.showZeroState || (page.name === 'addAnAttachmentFile' && !uploadedFiles.length) ? (
                    <SectionZeroState
                      button={{
                        name: 'Add Information',
                        onClick: () => handleModalPage({ type: 'edit-section-fields', page })
                      }}
                    />
                  ) : (
                    <SectionContent isMobile={isMobile}>
                      <RowsWrapper>
                        {page?.rows?.map((row, index) => (
                          <PageRowsPreview
                            key={`${row.name}-${index}`}
                            row={row}
                            page={page}
                            formData={legalEntityDocumentationData}
                            currentPageFormData={page?.formData ?? {}}
                            entityId={entityId}
                            contacts={contacts}
                            bankAccounts={bankAccounts}
                            uploadedFiles={uploadedFiles}
                          />
                        ))}
                      </RowsWrapper>
                    </SectionContent>
                  )}
                </Accordion>
              ))}
            </SectionsWrapper>
          </PaddingWrap>
        </>
      )}
    </InvestorDetailsLayout>
  );
};

const SectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 50px 0;
`;

const SectionContent = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  gap: 15px;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  padding: ${({ isMobile }) => (isMobile ? '0' : '20px')};
`;

const RowsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;
